import type { Dispatch, SetStateAction } from "react";
import { useCallback, use, useMemo, useRef, useState } from "react";
import { createApiResponse, withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type {
  Field,
  FieldIdToValueMap,
  GetRelatedObjectsResponse,
  ObjectTypeToLookupReferences,
} from "../../../../api/types/objectTypes";
import ObjectCardContext from "../../../entityFields/ObjectCardContext";
import useFieldValuesEditor, {
  type FieldValuesEditor,
  type FieldValuesSectionProps,
} from "../../../entityFields/useFieldValuesEditor";
import { withReadOnlyFields, type InvoiceCardSource } from "./invoiceCardSource";
import { collectRelatedCitations } from "./relatedObjectHelper";
import {
  areRelatedObjectRowsValid,
  getValidationErrorsByObjectType,
  getValidLookupOptionsOfRelatedObjects,
  type ValidationErrorsByRow,
} from "./relatedObjectsValidation";
import {
  getInitialRelatedObjectValuesState,
  getRelatedObjectFieldsByObjectType,
  getRelatedObjectsUpdateRequests,
  initRowsAction,
  type RelatedObjectValuesState,
} from "./relatedObjectValuesState";

const getObjectsAsLookupReferences = withErrorHandling(adminApi.getObjectsAsLookupReferences);

export interface InvoiceFieldValuesEditor extends FieldValuesEditor {
  relatedObjectValuesState: RelatedObjectValuesState;
  relatedObjectsResp: GetRelatedObjectsResponse | undefined;
  relatedObjectsError: ApiError | undefined;
  isFetchingRelatedObjects: boolean;
  lookupOptionsOfRelatedObjectsByObjectType: Record<string, ObjectTypeToLookupReferences> | undefined;
  relatedObjectFieldsByObjectType: Record<string, Field[]>;
  validationErrorsByObjectType: Record<string, ValidationErrorsByRow>;
  setRelatedObjectValuesState: Dispatch<SetStateAction<RelatedObjectValuesState>>;
  handleRelatedObjectValuesChange: Dispatch<SetStateAction<RelatedObjectValuesState>>;
  handleLookupOptionsNeeded: (lookupObjectTypes: string[]) => Promise<void>;
}

const useInvoiceFieldValuesEditor = (
  params: FieldValuesSectionProps,
  source: InvoiceCardSource | undefined
): InvoiceFieldValuesEditor => {
  const { objectType, objectId, fields, cardLayout, allowEditing, saveFieldValues } = params;

  const objectCard = use(ObjectCardContext);
  const { sendNotificationError } = useNotificationContext();

  const [relatedObjectValuesState, setRelatedObjectValuesState] = useState(getInitialRelatedObjectValuesState);
  const [lazyLookupOptions, setLazyLookupOptions] = useState<ObjectTypeToLookupReferences>({});
  const requestedLookupTypesRef = useRef(new Set<string>());

  const setHasUnsavedEdits = objectCard?.setHasUnsavedEdits;

  const saveFieldValuesWithRelatedObjects = (changedFieldValues: FieldIdToValueMap) =>
    saveFieldValues(
      changedFieldValues,
      getRelatedObjectsUpdateRequests(relatedObjectValuesState, cardLayout.relatedObjectGrids ?? [], objectId)
    );

  const editor = useFieldValuesEditor(
    { ...params, saveFieldValues: saveFieldValuesWithRelatedObjects },
    relatedObjectValuesState.dirtyObjectTypes.size > 0
  );

  const handleRelatedObjectValuesChange = useCallback(
    (action: SetStateAction<RelatedObjectValuesState>) => {
      setRelatedObjectValuesState(action);
      setHasUnsavedEdits?.(true);
    },
    [setHasUnsavedEdits]
  );

  const handleLookupOptionsNeeded = useCallback(
    async (lookupObjectTypes: string[]) => {
      const typesToLoad = [...new Set(lookupObjectTypes)].filter((type) => !requestedLookupTypesRef.current.has(type));
      typesToLoad.forEach((type) => requestedLookupTypesRef.current.add(type));

      await Promise.all(
        typesToLoad.map(async (type) => {
          const [references, error] = await getObjectsAsLookupReferences(type);
          if (error) {
            logError(error, "[useInvoiceFieldValuesEditor] getObjectsAsLookupReferences");
            sendNotificationError("Failed to load options");
            requestedLookupTypesRef.current.delete(type);
            return;
          }

          setLazyLookupOptions((prev) => ({ ...prev, [type]: references }));
        })
      );
    },
    [sendNotificationError]
  );

  const getRelatedObjects = useCallback(() => {
    if (!objectId || !cardLayout.relatedObjectGrids?.length) {
      return Promise.resolve(createApiResponse<GetRelatedObjectsResponse>({ relatedObjectsByType: [] }));
    }

    return adminApi.getRelatedObjects(objectType, objectId, {
      relatedObjects: cardLayout.relatedObjectGrids.map((layout) => ({
        objectType: layout.objectType,
        fieldIds: layout.fieldIds,
      })),
      includeLookupOptions: allowEditing,
    });
  }, [allowEditing, cardLayout.relatedObjectGrids, objectId, objectType]);

  const [relatedObjectsResp, relatedObjectsError, { isFetching: isFetchingRelatedObjects }] = useFetch(
    getRelatedObjects,
    {
      onSetData: (data) => {
        setRelatedObjectValuesState(initRowsAction(data.relatedObjectsByType));
        setHasUnsavedEdits?.(editor.hasUnsavedFieldEdits);
        objectCard?.setRelatedCitations(collectRelatedCitations(data.relatedObjectsByType));
      },
    }
  );

  const lookupOptionsOfRelatedObjectsByObjectType = useMemo(
    () =>
      editor.lookupOptionsByObjectType && relatedObjectsResp && allowEditing
        ? getValidLookupOptionsOfRelatedObjects(
            editor.fieldValuesState.fieldValues,
            fields,
            editor.lookupOptionsByObjectType,
            relatedObjectsResp.relatedObjectsByType.map((x) =>
              x.lookupOptions ? { ...x, lookupOptions: { ...x.lookupOptions, ...lazyLookupOptions } } : x
            )
          )
        : undefined,
    [
      allowEditing,
      editor.fieldValuesState.fieldValues,
      fields,
      editor.lookupOptionsByObjectType,
      relatedObjectsResp,
      lazyLookupOptions,
    ]
  );

  const relatedObjectFieldsByObjectType = useMemo(() => {
    if (!cardLayout.relatedObjectGrids || !relatedObjectsResp) {
      return {};
    }

    const fieldsByObjectType = getRelatedObjectFieldsByObjectType(
      cardLayout.relatedObjectGrids,
      relatedObjectsResp.relatedObjectsByType
    );
    const fieldsWithSource = source && fieldsByObjectType[source.relatedObjectType];
    return fieldsWithSource
      ? {
          ...fieldsByObjectType,
          [source.relatedObjectType]: withReadOnlyFields(fieldsWithSource, source.readOnlyRelatedFieldIds),
        }
      : fieldsByObjectType;
  }, [cardLayout.relatedObjectGrids, relatedObjectsResp, source]);

  const validationErrorsByObjectType = useMemo(
    () =>
      allowEditing
        ? getValidationErrorsByObjectType(
            relatedObjectValuesState.rowsByObjectType,
            relatedObjectFieldsByObjectType,
            lookupOptionsOfRelatedObjectsByObjectType
          )
        : {},
    [
      allowEditing,
      lookupOptionsOfRelatedObjectsByObjectType,
      relatedObjectFieldsByObjectType,
      relatedObjectValuesState.rowsByObjectType,
    ]
  );

  const handleCancelClick = () => {
    editor.handleCancelClick();
    setRelatedObjectValuesState(initRowsAction(relatedObjectsResp?.relatedObjectsByType ?? []));
  };

  return {
    ...editor,
    isSaveDisabled: editor.isSaveDisabled || !areRelatedObjectRowsValid(validationErrorsByObjectType),
    isBusy: editor.isBusy || isFetchingRelatedObjects,
    handleCancelClick,
    relatedObjectValuesState,
    relatedObjectsResp,
    relatedObjectsError,
    isFetchingRelatedObjects,
    lookupOptionsOfRelatedObjectsByObjectType,
    relatedObjectFieldsByObjectType,
    validationErrorsByObjectType,
    setRelatedObjectValuesState,
    handleRelatedObjectValuesChange,
    handleLookupOptionsNeeded,
  };
};

export default useInvoiceFieldValuesEditor;
